import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import WanderLogo from './WanderLogo';

const LoginModal = () => {
  const { showLoginModal, loginMessage, closeLoginModal, signIn, error, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  // Don't show if already authenticated or modal is closed
  if (!showLoginModal || isAuthenticated) return null;

  // Check if this is a required login (has a message) - can't be dismissed
  const isRequired = !!loginMessage;

  const handleGoogleSignIn = async () => {
    if (!acceptedTerms) {
      setLocalError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    if (!ageConfirmed) {
      setLocalError('Please confirm you are 16 years of age or older.');
      return;
    }

    setIsLoading(true);
    setLocalError(null);
    try {
      await signIn();
      // Redirect happens automatically
    } catch (err) {
      setLocalError(err.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: "'Batangas', sans-serif" }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-8 text-center">
          <div className="flex justify-center mb-2">
            <WanderLogo size="lg" showText={false} />
          </div>
          {/* Tagline — matches the referral-code entry page + app header */}
          <p className="text-emerald-100 text-sm mb-3" style={{ fontFamily: "'Raleway', sans-serif", letterSpacing: '-0.01em' }}>
            plan less. explore more.
          </p>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Batangas', sans-serif" }}>
            Welcome to arrivo
          </h2>
          <p className="text-emerald-100 mt-1" style={{ fontFamily: "'Batangas', sans-serif" }}>Your AI-powered travel companion</p>
        </div>

        {/* Body */}
        <div className="px-6 py-8">
          {/* Custom message if provided */}
          {loginMessage && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">{loginMessage}</p>
            </div>
          )}

          {/* Error message */}
          {displayError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{displayError}</p>
            </div>
          )}

          {/* Features list */}
          <div className="mb-6 space-y-3">
            <div className="flex items-center gap-3 text-gray-600">
              <span className="text-emerald-500">✓</span>
              <span>Save and revisit your itineraries</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <span className="text-emerald-500">✓</span>
              <span>Personalized travel recommendations</span>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <span className="text-emerald-500">✓</span>
              <span>Sync preferences across devices</span>
            </div>
          </div>

          {/* Terms & Privacy Consent */}
          <label className="flex items-start gap-3 mb-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => {
                setAcceptedTerms(e.target.checked);
                if (e.target.checked) setLocalError(null);
              }}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-600">
              I agree to the{' '}
              <a
                href="https://arrivotravel.ai/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 underline hover:text-emerald-700"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://arrivotravel.ai/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 underline hover:text-emerald-700"
              >
                Privacy Policy
              </a>
            </span>
          </label>

          {/* Age confirmation checkbox */}
          <label className="flex items-start gap-3 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => {
                setAgeConfirmed(e.target.checked);
                if (e.target.checked) setLocalError(null);
              }}
              className="mt-1 w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-sm text-gray-600">
              I confirm I am 16 years of age or older
            </span>
          </label>

          {/* Google Sign In Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading || !acceptedTerms || !ageConfirmed}
            className={`w-full flex items-center justify-center gap-3 px-6 py-3 border rounded-lg transition-colors ${
              acceptedTerms && ageConfirmed
                ? 'border-gray-300 hover:bg-gray-50 cursor-pointer'
                : 'border-gray-200 bg-gray-50 cursor-not-allowed'
            } disabled:opacity-50`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            <span className={`font-medium ${acceptedTerms && ageConfirmed ? 'text-gray-700' : 'text-gray-400'}`}>
              {isLoading ? 'Signing in...' : 'Continue with Google'}
            </span>
          </button>

          {/* Invite-only notice */}
          <p className="mt-4 text-center text-xs text-gray-500">
            Currently invite-only. Contact us for access.
          </p>
        </div>

        {/* Footer - only show "Continue as guest" if login is not required */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between">
            {!isRequired ? (
              <button
                onClick={closeLoginModal}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Continue as guest
              </button>
            ) : (
              <span className="text-xs text-gray-400">Sign in required to continue</span>
            )}
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <a href="https://arrivotravel.ai/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Privacy</a>
              <a href="https://arrivotravel.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">Terms</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
